import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check token and load user on mount
  useEffect(() => {
    const loadUser = async () => {
      const savedToken = localStorage.getItem('token');
      if (!savedToken) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
          headers: {
            'Authorization': `Bearer ${savedToken}`
          }
        });

        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          setToken(savedToken);
        } else {
          // Token invalid, clear it
          localStorage.removeItem('token');
          setToken(null);
        }
      } catch (err) {
        console.error('Error loading user:', err);
        localStorage.removeItem('token');
        setToken(null);
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      // Handle rate limiting (429) separately
      if (response.status === 429) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Слишком много попыток входа. Попробуйте через 5 минут.');
      }

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        // JSON parsing failed - likely invalid credentials or server error
        throw new Error('Вы ввели неверные данные, ошибка в поле Email или неверно введён пароль');
      }

      if (!response.ok) {
        throw new Error(data.detail || 'Вы ввели неверные данные, ошибка в поле Email или неверно введён пароль');
      }

      localStorage.setItem('token', data.access_token);
      setToken(data.access_token);
      
      // Fetch full user profile including is_admin
      try {
        const profileResponse = await fetch(`${API_URL}/api/auth/me`, {
          headers: {
            'Authorization': `Bearer ${data.access_token}`
          }
        });
        
        if (profileResponse.ok) {
          const fullUserData = await profileResponse.json();
          setUser(fullUserData);
        } else {
          setUser(data.user);
        }
      } catch {
        // If profile fetch fails, use login response user data
        setUser(data.user);
      }
      
      return { success: true };
    } catch (err) {
      // Handle network errors (Failed to fetch)
      let errorMessage = err.message;
      if (err.message === 'Failed to fetch' || err.name === 'TypeError') {
        errorMessage = 'Сервис временно недоступен, ведутся технические работы. Попробуйте авторизоваться позже.';
      } else if (err.message.includes("body stream already read") || err.message.includes("JSON")) {
        errorMessage = 'Вы ввели неверные данные, ошибка в поле Email или неверно введён пароль';
      }
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, []);

  const register = useCallback(async (email, name, password, pdConsent) => {
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          email, 
          name, 
          password, 
          pd_consent: pdConsent 
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Ошибка регистрации');
      }

      localStorage.setItem('token', data.access_token);
      setToken(data.access_token);
      setUser(data.user);
      return { success: true };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  }, []);

  const authFetch = useCallback(async (url, options = {}) => {
    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`
    };

    const response = await fetch(url, { ...options, headers });

    if (response.status === 401) {
      logout();
      throw new Error('Сессия истекла. Пожалуйста, войдите снова.');
    }

    return response;
  }, [token, logout]);

  const value = {
    user,
    token,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    authFetch
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
